import { getSchoolHeader, switchCarrierView } from "./utils/carrier-view";
export const API_BASE = (window as any).__API_URL__ ?? "http://localhost:8080";
let tokenGetter: (() => string | undefined) | null = null;

export function setTokenGetter(fn: () => string | undefined) { tokenGetter = fn; }

/** Token und, bei Träger-Usern in einer freigegebenen Schule, die gewählte Schule. */
function authHeaders(base: Record<string, string> = {}): Record<string, string> {
  const token = tokenGetter?.();
  const headers = { ...base };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const school = getSchoolHeader();
  if (school) headers["X-Schule-Id"] = school;
  return headers;
}

async function request<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers = authHeaders({ "Content-Type": "application/json", ...(opts.headers as any || {}) });
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    // Freigabe für die gewählte Schule entzogen oder Schule getrennt: zurück in den Träger-Bereich
    if (res.status === 403 && body.code === "KEIN_TRAEGER_ZUGRIFF" && getSchoolHeader()) {
      switchCarrierView({ mode: "traeger" }, "#/traeger");
    }
    const err = new Error(body.error || body.message || `API Fehler: ${res.status}`);
    // Der Status bleibt am Fehler hängen, damit Aufrufer einen fachlichen Konflikt
    // (409, etwa "wird noch verwendet") von einem echten Fehler unterscheiden können.
    (err as any).status = res.status;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function uploadFile<T>(path: string, file: File): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  const headers = authHeaders();
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: formData });
  const body = await res.json();
  if (!res.ok) throw body;
  return body;
}

async function uploadFileWithFields<T>(
  path: string,
  file: File,
  fields: Record<string, string>
): Promise<T> {
  const formData = new FormData();
  formData.append("file", file);
  for (const [k, v] of Object.entries(fields)) formData.append(k, v);
  const headers = authHeaders();
  const res = await fetch(`${API_BASE}${path}`, { method: "POST", headers, body: formData });
  const body = await res.json();
  if (!res.ok) throw body;
  return body;
}

async function downloadFile(path: string, filename: string): Promise<void> {
  const headers = authHeaders();
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new Error("Download fehlgeschlagen");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function downloadJsonPost(
  path: string,
  body: any,
  filename: string
): Promise<void> {
  const headers = authHeaders({ "Content-Type": "application/json" });
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(errBody.error || errBody.message || "Download fehlgeschlagen");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/** Request ohne Auth-Header (für öffentliche Endpoints) */
async function requestPublic<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json", ...(opts.headers as any || {}) };
  const res = await fetch(`${API_BASE}${path}`, { ...opts, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(body.error || body.message || `API Fehler: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

import type { SchoolClass, Student, Practice, Department, AdminSchoolDepartment, DepartmentGroup, PlanningPeriod, Assignment, Vacation, UserInfo, School, SchoolUser, PlatformStats, CsvImportResult, StudentHoursResponse, SchoolSettings, StudentProfil, StudentPlanungData, StudentPreferences, StudentWishesResponse, Subscription, AdminStudentAssignment, StudentDocument, SharedDocument, SharedDistributeTarget, PracticeOrg, PracticeOrgAdmin, PracticeOrgAdminDetail, PracticePlanningAssignment, SchuelerSteckbrief, PracticeOrgAssignmentSteckbrief, PracticeUser, Kreisverband, KreisverbandInput, MailNotificationsResponse, MailTriggerState, PlanningProgressResponse, TimeSlot, Lecturer, LecturerAvailability, LecturerException, TrainingPrograms, Subject, CourseModel, SchoolClosure, Lesson, LessonSeries, LessonConflict, StudentTimetable, Room, RoomFeature, RoomAvailability, LessonStatus, StudentAbsence, AbsenceKind, ClassAttendance, StudentAbsenceReport, LessonReport, WishReport, CarrierInfo, CarrierSchool, CarrierReport, CarrierMember, CarrierCandidate, SchoolCarrierStatus, AdminCarrier, AdminCarrierDetail, CarrierRequest, CarrierCodePreview, Curriculum, CurriculumTemplateInfo, CurriculumTemplate, SeriesPreview } from "./types";

/** Basis der Lehrplan-Routen: der Lehrplan hängt am Ausbildungsmodell. */
const LP = "/api/stundenplan/ausbildungsmodelle";

/** Kostenpflichtige Module, die über Stripe gebucht werden können. */
export type BillingProduct = "schuelerPortal" | "planung" | "stundenplan";

export const api = {
  me: () => request<UserInfo>("/api/me"),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ success: boolean }>("/api/me/passwort", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  geocode: {
    // Geokodierung läuft über das Backend: der Geocoder liegt im eigenen VPC und
    // ist aus dem Browser nicht erreichbar. Adressen gehen damit nicht mehr an
    // einen Dritten.
    lookup: (street?: string, houseNo?: string, plz?: string, ort?: string) => {
      const p = new URLSearchParams();
      if (street) p.set("street", street);
      if (houseNo) p.set("houseNo", houseNo);
      if (plz) p.set("plz", plz);
      if (ort) p.set("ort", ort);
      return request<{ lat: number | null; lng: number | null }>(`/api/geocode?${p}`);
    },
  },
  classes: {
    list: () => request<SchoolClass[]>("/api/klassen"),
    get: (id: number) => request<SchoolClass>(`/api/klassen/${id}`),
    create: (d: any) => request<SchoolClass>("/api/klassen", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: any) => request<SchoolClass>(`/api/klassen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    delete: (id: number) => request<void>(`/api/klassen/${id}`, { method: "DELETE" }),
    departments: (classId: number) => request<Department[]>(`/api/klassen/${classId}/fachbereiche`),
    /** Namensliste der Klasse. Die Klassenliste selbst zeigt nur eine Zählung. */
    students: (classId: number) =>
      request<{ id: number; firstName: string; lastName: string; email: string | null }[]>(
        `/api/klassen/${classId}/schueler`),
  },
  /** DRK-Kreisverbände; ohne freigeschaltetes Feature antwortet das Backend mit 404. */
  kreisverbaende: {
    list: () => request<Kreisverband[]>("/api/kreisverbaende"),
    create: (d: KreisverbandInput & { name: string }) => request<Kreisverband>("/api/kreisverbaende", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: KreisverbandInput) => request<Kreisverband>(`/api/kreisverbaende/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    remove: (id: number) => request<void>(`/api/kreisverbaende/${id}`, { method: "DELETE" }),
  },
  students: {
    list: (classId?: number) => request<Student[]>(`/api/schueler${classId ? `?classId=${classId}` : ""}`),
    create: (d: any) => request<Student>("/api/schueler", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: any) => request<Student>(`/api/schueler/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    delete: (id: number) => request<void>(`/api/schueler/${id}`, { method: "DELETE" }),
    csvTemplate: () => downloadFile("/api/schueler/csv-template", "schueler-vorlage.csv"),
    csvImport: (file: File) => uploadFile<CsvImportResult>("/api/schueler/csv-import", file),
    hours: (id: number) => request<StudentHoursResponse>(`/api/schueler/${id}/stunden`),
    /** Fehlzeiten-Nachweis: versäumte gegen tatsächlich gehaltene Stunden. */
    absences: (id: number) => request<StudentAbsenceReport>(`/api/schueler/${id}/fehlzeiten`),
    savePoolDistribution: (studentId: number, allocations: Record<string, number>) =>
      request<{ success: boolean }>(`/api/schueler/${studentId}/pool-verteilung`, { method: "PUT", body: JSON.stringify(allocations) }),
    deletePoolDistribution: (studentId: number) =>
      request<{ success: boolean }>(`/api/schueler/${studentId}/pool-verteilung`, { method: "DELETE" }),
    planung: (id: number) => request<AdminStudentAssignment[]>(`/api/schueler/${id}/planung`),
    // mode "invite": Schüler bekommt eine Einladung und vergibt sein Passwort selbst.
    // mode "password": Erstkennwort wird der Verwaltung einmalig angezeigt.
    // emailQueued: Einladung ist eingereiht, der Mail-Worker verschickt sie gleich.
    // Bei emailSent === false liegt das Kennwort als Notfallweg trotzdem bei.
    toggleLogin: (id: number, enabled: boolean, email?: string) =>
      request<{
        mode?: "invite" | "password";
        emailQueued?: boolean;
        emailSent?: boolean;
        password?: string;
        email?: string;
        success?: boolean;
      }>(`/api/schueler/${id}/login`, { method: "PUT", body: JSON.stringify({ enabled, email }) }),
    resetPassword: (id: number) =>
      request<{ success: boolean; message: string }>(`/api/schueler/${id}/reset-password`, { method: "POST" }),
  },
  studentDocuments: {
    list: (studentId: number) => request<StudentDocument[]>(`/api/schueler/${studentId}/dokumente`),
    upload: (studentId: number, file: File) =>
      uploadFile<StudentDocument>(`/api/schueler/${studentId}/dokumente`, file),
    download: (studentId: number, docId: number, filename: string) =>
      downloadFile(`/api/schueler/${studentId}/dokumente/${docId}/download`, filename),
    delete: (studentId: number, docId: number) =>
      request<void>(`/api/schueler/${studentId}/dokumente/${docId}`, { method: "DELETE" }),
    rename: (studentId: number, docId: number, originalName: string) =>
      request<StudentDocument>(`/api/schueler/${studentId}/dokumente/${docId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ originalName }),
      }),
    merge: (studentId: number, docIds: number[], filename: string) =>
      downloadJsonPost(
        `/api/schueler/${studentId}/dokumente/merge`,
        { docIds },
        filename
      ),
  },
  myDocuments: {
    list: () => request<StudentDocument[]>("/api/schueler-app/dokumente"),
    upload: (file: File) =>
      uploadFile<StudentDocument>("/api/schueler-app/dokumente", file),
    download: (docId: number, filename: string) =>
      downloadFile(`/api/schueler-app/dokumente/${docId}/download`, filename),
    delete: (docId: number) =>
      request<void>(`/api/schueler-app/dokumente/${docId}`, { method: "DELETE" }),
    rename: (docId: number, originalName: string) =>
      request<StudentDocument>(`/api/schueler-app/dokumente/${docId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ originalName }),
      }),
  },
  practiceDocuments: {
    list: (practiceId: number) =>
      request<StudentDocument[]>(`/api/praktikumsstellen/${practiceId}/dokumente`),
    upload: (practiceId: number, file: File) =>
      uploadFile<StudentDocument>(`/api/praktikumsstellen/${practiceId}/dokumente`, file),
    download: (practiceId: number, docId: number, filename: string) =>
      downloadFile(`/api/praktikumsstellen/${practiceId}/dokumente/${docId}/download`, filename),
    delete: (practiceId: number, docId: number) =>
      request<void>(`/api/praktikumsstellen/${practiceId}/dokumente/${docId}`, { method: "DELETE" }),
    rename: (practiceId: number, docId: number, originalName: string) =>
      request<StudentDocument>(`/api/praktikumsstellen/${practiceId}/dokumente/${docId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ originalName }),
      }),
    merge: (practiceId: number, docIds: number[], filename: string) =>
      downloadJsonPost(
        `/api/praktikumsstellen/${practiceId}/dokumente/merge`,
        { docIds },
        filename
      ),
  },
  sharedDocuments: {
    list: () => request<SharedDocument[]>("/api/verteilte-dokumente"),
    distribute: (file: File, target: SharedDistributeTarget) => {
      const fields: Record<string, string> = { targetKind: target.targetKind };
      if (target.targetKind === "student") {
        fields.scope = target.scope;
        if (target.scope === "classes") fields.classIds = JSON.stringify(target.classIds);
      } else {
        fields.practiceIds = JSON.stringify(target.practiceIds);
      }
      return uploadFileWithFields<SharedDocument>("/api/verteilte-dokumente", file, fields);
    },
    candidates: (id: number) =>
      request<{ targetKind: "student" | "practice"; candidates: { id: number; label: string; sub: string | null }[] }>(
        `/api/verteilte-dokumente/${id}/candidates`
      ),
    addRecipients: (id: number, ids: number[]) =>
      request<{ added: number }>(`/api/verteilte-dokumente/${id}/add`, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    download: (id: number, filename: string) =>
      downloadFile(`/api/verteilte-dokumente/${id}/download`, filename),
    withdraw: (id: number) =>
      request<void>(`/api/verteilte-dokumente/${id}`, { method: "DELETE" }),
  },
  practices: {
    list: (dept?: string, includeInactive?: boolean) => {
      const params = new URLSearchParams();
      if (dept) params.set("department", dept);
      if (includeInactive) params.set("includeInactive", "true");
      const qs = params.toString();
      return request<Practice[]>(`/api/praktikumsstellen${qs ? `?${qs}` : ""}`);
    },
    create: (d: any) => request<Practice>("/api/praktikumsstellen", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: any) => request<Practice>(`/api/praktikumsstellen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    delete: (id: number) => request<void>(`/api/praktikumsstellen/${id}`, { method: "DELETE" }),
    toggle: (id: number) => request<any>(`/api/praktikumsstellen/${id}/toggle-active`, { method: "PATCH" }),
    csvTemplate: () => downloadFile("/api/praktikumsstellen/csv-template", "praktikumsstellen-vorlage.csv"),
    csvImport: (file: File) => uploadFile<CsvImportResult>("/api/praktikumsstellen/csv-import", file),
    assignments: (id: number, periodId?: number) => {
      const qs = periodId ? `?periodId=${periodId}` : "";
      return request<any[]>(`/api/praktikumsstellen/${id}/zuweisungen${qs}`);
    },
    lookupByCode: (code: string) =>
      request<{ org: PracticeOrg; alreadyLinkedTo: { id: number; name: string } | null }>(
        `/api/praktikumsstellen/lookup-code?code=${encodeURIComponent(code)}`
      ),
    connect: (id: number, code: string) =>
      request<Practice>(`/api/praktikumsstellen/${id}/connect`, { method: "POST", body: JSON.stringify({ code }) }),
    disconnect: (id: number) =>
      request<Practice>(`/api/praktikumsstellen/${id}/disconnect`, { method: "POST" }),
  },
  users: {
    list: () => request<SchoolUser[]>("/api/benutzer"),
    create: (d: any) => request<SchoolUser>("/api/benutzer", { method: "POST", body: JSON.stringify(d) }),
    update: (id: string, d: any) => request<any>(`/api/benutzer/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    toggle: (id: string, enabled: boolean) => request<any>(`/api/benutzer/${id}/toggle`, { method: "PUT", body: JSON.stringify({ enabled }) }),
    resetPassword: (id: string) => request<any>(`/api/benutzer/${id}/reset-password`, { method: "PUT" }),
    delete: (id: string) => request<void>(`/api/benutzer/${id}`, { method: "DELETE" }),
  },
  departments: () => request<Department[]>("/api/fachbereiche"),
  // Träger-Bereich
  carrier: {
    get: () => request<CarrierInfo>("/api/traeger"),
    update: (name: string) => request<{ success: boolean }>("/api/traeger", { method: "PUT", body: JSON.stringify({ name }) }),
    regenerateCode: () => request<{ code: string }>("/api/traeger/code-neu", { method: "POST" }),
    setDefaultSchool: (schoolId: number | null) =>
      request<{ success: boolean; defaultSchoolId: number | null }>("/api/traeger/standard-schule", { method: "PUT", body: JSON.stringify({ schoolId }) }),
    delete: (password: string) => request<{ success: boolean }>("/api/traeger", { method: "DELETE", body: JSON.stringify({ password }) }),
    schools: () => request<CarrierSchool[]>("/api/traeger/schulen"),
    createSchool: (d: { name: string; address: string; plz: string; ort: string; email: string; agbAccepted: boolean; avvAccepted: boolean }) =>
      request<{ id: number }>("/api/traeger/schulen", { method: "POST", body: JSON.stringify(d) }),
    removeSchool: (id: number) => request<{ success: boolean }>(`/api/traeger/schulen/${id}`, { method: "DELETE" }),
    users: () => request<{ users: CarrierMember[]; candidates: CarrierCandidate[]; schools: { id: number; name: string }[] }>("/api/traeger/benutzer"),
    setUserSchools: (userId: string, schoolIds: number[], homeSchoolId?: number) =>
      request<{ success: boolean; schoolIds: number[] }>(`/api/traeger/benutzer/${encodeURIComponent(userId)}`, { method: "PUT", body: JSON.stringify({ schoolIds, homeSchoolId }) }),
    report: () => request<CarrierReport>("/api/traeger/auswertung"),
    // Anfragen zum Verbinden, nur Haupt-Trägeradmin
    requests: () => request<CarrierRequest[]>("/api/traeger/anfragen"),
    acceptRequest: (id: number) => request<{ success: boolean }>(`/api/traeger/anfragen/${id}/annehmen`, { method: "POST" }),
    rejectRequest: (id: number) => request<{ success: boolean }>(`/api/traeger/anfragen/${id}/ablehnen`, { method: "POST" }),
  },
  settings: {
    get: () => request<SchoolSettings>("/api/einstellungen"),
    update: (d: any) => request<any>("/api/einstellungen", { method: "PUT", body: JSON.stringify(d) }),
    departmentGroups: () => request<DepartmentGroup[]>("/api/einstellungen/fachbereich-gruppen"),
    changeDepartmentGroup: (groupId: number) => request<any>("/api/einstellungen/fachbereich-gruppe", { method: "PUT", body: JSON.stringify({ departmentGroupId: groupId }) }),
    updateDepartment: (departmentId: string, d: Partial<Department>) => request<Department>(`/api/einstellungen/fachbereiche/${encodeURIComponent(departmentId)}`, { method: "PUT", body: JSON.stringify(d) }),
    addDepartment: (d: Omit<Department, "id"> & { customGroupId?: number; extendsGroupId?: number }) => request<Department>("/api/einstellungen/fachbereiche", { method: "POST", body: JSON.stringify(d) }),
    deleteDepartment: (departmentId: string) => request<void>(`/api/einstellungen/fachbereiche/${encodeURIComponent(departmentId)}`, { method: "DELETE" }),
    savePoolDistribution: (groupId: number, allocations: Record<string, number>) =>
      request<{ success: boolean }>("/api/einstellungen/pool-verteilung", { method: "PUT", body: JSON.stringify({ groupId, allocations }) }),
    setGroupActive: (groupId: number, isCustom: boolean, active: boolean, opts?: { reassignClassesToGroupId?: number; reassignIsCustom?: boolean }) => request<{ success: boolean }>("/api/einstellungen/fachbereichsgruppe-active", { method: "PUT", body: JSON.stringify({ groupId, isCustom, active, ...opts }) }),
    getGroupDeactivationImpact: (groupId: number, isCustom: boolean) => request<{ assignmentCount: number; affectedClasses: { id: number; name: string }[]; otherGroups: { id: number; name: string }[] }>(`/api/einstellungen/group-deactivation-impact?groupId=${groupId}&isCustom=${isCustom ? 1 : 0}`),
    createCustomGroup: (name: string) => request<{ id: number; name: string; isCustom: boolean }>("/api/einstellungen/custom-gruppen", { method: "POST", body: JSON.stringify({ name }) }),
    updateCustomGroup: (id: number, name: string) => request<void>(`/api/einstellungen/custom-gruppen/${id}`, { method: "PUT", body: JSON.stringify({ name }) }),
    deleteCustomGroup: (id: number) => request<void>(`/api/einstellungen/custom-gruppen/${id}`, { method: "DELETE" }),
    groupsWithOverrides: () => request<DepartmentGroup[]>("/api/einstellungen/fachbereiche-uebersicht"),
    deleteSchool: (password: string) =>
      request<void>("/api/einstellungen/schule", { method: "DELETE", body: JSON.stringify({ password }) }),
    // Schulträger aus Sicht der eigenen Schule
    carrier: {
      status: () => request<SchoolCarrierStatus>("/api/einstellungen/traeger"),
      found: (name: string) => request<{ id: number }>("/api/einstellungen/traeger/gruenden", { method: "POST", body: JSON.stringify({ name }) }),
      checkCode: (code: string) => request<CarrierCodePreview>(`/api/einstellungen/traeger/code-pruefen?code=${encodeURIComponent(code)}`),
      // Verbunden wird erst, wenn der Haupt-Trägeradmin die Anfrage annimmt
      sendRequest: (code: string, consent: boolean) =>
        request<{ success: boolean; name: string }>("/api/einstellungen/traeger/anfrage", { method: "POST", body: JSON.stringify({ code, consent }) }),
      withdrawRequest: () => request<{ success: boolean }>("/api/einstellungen/traeger/anfrage", { method: "DELETE" }),
      leave: () => request<{ success: boolean }>("/api/einstellungen/traeger/austreten", { method: "POST" }),
    },
    toggleKapazitaetProZeitraum: (enabled: boolean) =>
      request<{ success: boolean; enabled: boolean }>("/api/einstellungen/kapazitaet-pro-zeitraum", { method: "PUT", body: JSON.stringify({ enabled }) }),
    toggleVorplanung: (enabled: boolean) =>
      request<{ success: boolean; enabled: boolean }>("/api/einstellungen/vorplanung", { method: "PUT", body: JSON.stringify({ enabled }) }),
    toggleAbrechnung: (enabled: boolean) =>
      request<{ success: boolean; enabled: boolean }>("/api/einstellungen/abrechnung", { method: "PUT", body: JSON.stringify({ enabled }) }),
    stundenplanVorplanung: (enabled: boolean, defaultVisible?: boolean, defaultPreliminary?: boolean) =>
      request<{ success: boolean }>("/api/einstellungen/stundenplan-vorplanung",
        { method: "PUT", body: JSON.stringify({ enabled, defaultVisible, defaultPreliminary }) }),
    stundenplanSkipPractice: (enabled: boolean) =>
      request<{ success: boolean }>("/api/einstellungen/stundenplan-praktikum",
        { method: "PUT", body: JSON.stringify({ enabled }) }),
    updateVorplanungDefaults: (defaultVisible: boolean, defaultPreliminary: boolean) =>
      request<{ success: boolean; defaultVisible: boolean; defaultPreliminary: boolean }>(
        "/api/einstellungen/vorplanung-defaults",
        { method: "PUT", body: JSON.stringify({ defaultVisible, defaultPreliminary }) },
      ),
    setStudentPortalFeature: (key: string, enabled: boolean) =>
      request<{ success: boolean; features: Record<string, boolean> }>(
        "/api/einstellungen/schueler-portal-funktionen",
        { method: "PUT", body: JSON.stringify({ key, enabled }) },
      ),
    mailNotifications: {
      get: () => request<MailNotificationsResponse>("/api/einstellungen/benachrichtigungen"),
      update: (
        key: string,
        payload: { enabled?: boolean; subject?: string; body?: string; events?: Record<string, boolean> },
      ) =>
        request<{ success: boolean; settings: Record<string, MailTriggerState> }>(
          `/api/einstellungen/benachrichtigungen/${encodeURIComponent(key)}`,
          { method: "PUT", body: JSON.stringify(payload) },
        ),
      resetText: (key: string) =>
        request<{ success: boolean; settings: Record<string, MailTriggerState> }>(
          `/api/einstellungen/benachrichtigungen/${encodeURIComponent(key)}/text`,
          { method: "DELETE" },
        ),
      sendTest: (key: string) =>
        request<{ success: boolean; recipient: string }>(
          `/api/einstellungen/benachrichtigungen/${encodeURIComponent(key)}/test`,
          { method: "POST" },
        ),
    },
    completeOnboardingStep: (stepId: string) =>
      request<{ success: boolean; onboardingSteps: string[] }>("/api/einstellungen/onboarding-step", { method: "PUT", body: JSON.stringify({ stepId }) }),
    completeOnboarding: () =>
      request<{ success: boolean }>("/api/einstellungen/onboarding-complete", { method: "PUT" }),
    exportData: () => downloadFile("/api/einstellungen/export", `meduvio-export-${new Date().toISOString().slice(0, 10)}.zip`),
  },
  periods: {
    list: () => request<PlanningPeriod[]>("/api/planung/zeitraeume"),
    create: (d: any) => request<PlanningPeriod>("/api/planung/zeitraeume", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: any) => request<PlanningPeriod>(`/api/planung/zeitraeume/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    delete: (id: number) => request<void>(`/api/planung/zeitraeume/${id}`, { method: "DELETE" }),
    makeAllVisible: (periodId: number) => request<{ updated: number }>(`/api/planung/zeitraeume/${periodId}/zuweisungen/sichtbar-machen`, { method: "POST" }),
    confirmAll: (periodId: number) => request<{ updated: number }>(`/api/planung/zeitraeume/${periodId}/zuweisungen/bestaetigen`, { method: "POST" }),
  },
  assignments: {
    list: (periodId: number) => request<Assignment[]>(`/api/planung/zeitraeume/${periodId}/zuweisungen`),
    create: (d: any) => request<Assignment>("/api/planung/zuweisungen", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: any) => request<Assignment>(`/api/planung/zuweisungen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    delete: (id: number) => request<void>(`/api/planung/zuweisungen/${id}`, { method: "DELETE" }),
  },
  // Protokoll vor einem Download mit Schülerstammdaten; ohne Eintrag kein Download
  downloadLog: {
    create: (d: {
      periodId: number; export: "praktikumsstellen"; format: "xlsx" | "pdf";
      fields: ("address" | "phone" | "email")[]; studentCount: number; practiceCount: number;
    }) => request<{ success: boolean }>("/api/planung/download-protokoll", { method: "POST", body: JSON.stringify(d) }),
  },
  // Stundenfortschritt aller Schüler auf einmal (Matrix-Ansicht in der Planung)
  progress: {
    list: () => request<PlanningProgressResponse>("/api/planung/fortschritt"),
  },
  autoPlanning: {
    run: (periodId: number, config: any) => request<any>(`/api/planung/zeitraeume/${periodId}/auto-planung`, { method: "POST", body: JSON.stringify(config) }),
    confirm: (periodId: number, data: any) => request<{ created: number; message: string }>(`/api/planung/zeitraeume/${periodId}/auto-planung/bestaetigen`, { method: "POST", body: JSON.stringify(data) }),
  },
  vacations: {
    list: (periodId: number) => request<Vacation[]>(`/api/planung/zeitraeume/${periodId}/ferien`),
    create: (d: any) => request<Vacation>("/api/planung/ferien", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: any) => request<Vacation>(`/api/planung/ferien/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    delete: (id: number) => request<void>(`/api/planung/ferien/${id}`, { method: "DELETE" }),
  },
  /** Ferien für alle Zeiträume. Datumsbasiert ("YYYY-MM-DD"), das Backend erweitert auf ganze Wochen. */
  sharedVacations: {
    create: (d: { name: string; startDate: string; endDate: string }) =>
      request<void>("/api/planung/gemeinsame-ferien", { method: "POST", body: JSON.stringify(d) }),
    update: (id: number, d: { name: string; startDate: string; endDate: string }) =>
      request<void>(`/api/planung/gemeinsame-ferien/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    /** Löscht die Ferien in allen Zeiträumen. */
    delete: (id: number) => request<void>(`/api/planung/gemeinsame-ferien/${id}`, { method: "DELETE" }),
    /** Entfernt die Ferien nur aus einem Zeitraum. */
    excludeFromPeriod: (id: number, periodId: number) =>
      request<void>(`/api/planung/gemeinsame-ferien/${id}/zeitraeume/${periodId}`, { method: "DELETE" }),
    /** Entfernt die Ferien nur bei einem Schüler in einem Zeitraum. */
    excludeStudent: (id: number, periodId: number, studentId: number) =>
      request<void>(`/api/planung/gemeinsame-ferien/${id}/zeitraeume/${periodId}/schueler/${studentId}`, { method: "DELETE" }),
  },
  // Schüler-App-Endpunkte (für eingeloggte Schüler)
  schuelerApp: {
    profil: () => request<StudentProfil>("/api/schueler-app/profil"),
    planung: () => request<StudentPlanungData>("/api/schueler-app/planung"),
    stundenplan: (von: string, bis: string) =>
      request<StudentTimetable>(`/api/schueler-app/stundenplan?von=${von}&bis=${bis}`),
    absences: {
      list: () => request<StudentAbsence[]>("/api/schueler-app/abwesenheiten"),
      create: (d: { startDate: string; endDate?: string; startTime?: string | null; endTime?: string | null; attestVorgelegt?: boolean }) =>
        request<StudentAbsence>("/api/schueler-app/abwesenheiten", { method: "POST", body: JSON.stringify(d) }),
      retract: (id: number) =>
        request<StudentAbsence>(`/api/schueler-app/abwesenheiten/${id}/zuruecknehmen`, { method: "PATCH" }),
    },
    fehlzeiten: () =>
      request<{ absentHours: number; heldHours: number }>("/api/schueler-app/fehlzeiten"),
    fachbereiche: () => request<{ id: string; nameDe: string; color: string; shortName: string | null; requiredHours: number; poolHours: number }[]>("/api/schueler-app/fachbereiche"),
    praktika: (department?: string) => {
      const qs = department ? `?department=${encodeURIComponent(department)}` : "";
      return request<{ id: number; name: string; plz: string | null; ort: string | null; street: string | null; houseNo: string | null; contactPersonStudents: string | null; phoneStudents: string | null; emailStudents: string | null; commentStudents: string | null; latitude: number | null; longitude: number | null; departments: { id: string; nameDe: string; color: string }[] }[]>(`/api/schueler-app/praktika${qs}`);
    },
    wuensche: () => request<StudentWishesResponse>("/api/schueler-app/wuensche"),
    wuenschePraktika: (dept: string) => request<{ id: number; name: string; ort?: string }[]>(`/api/schueler-app/wuensche/praktika/${encodeURIComponent(dept)}`),
    wuenscheSave: (dept: string, practiceIds: number[]) => request<{ ok: boolean }>(`/api/schueler-app/wuensche/${encodeURIComponent(dept)}`, { method: "PUT", body: JSON.stringify({ practiceIds }) }),
    steckbrief: () => request<SchuelerSteckbrief>("/api/schueler-app/steckbrief"),
    steckbriefSave: (text: string, released: boolean) =>
      request<SchuelerSteckbrief>("/api/schueler-app/steckbrief", { method: "PUT", body: JSON.stringify({ text, released }) }),
  },
  schuelerWuensche: (studentId: number) => request<StudentPreferences>(`/api/schueler/${studentId}/wuensche`),
  schuelerSteckbrief: (studentId: number) => request<SchuelerSteckbrief>(`/api/schueler/${studentId}/steckbrief`),
  // Billing / Abonnements (Schul-Admin)
  billing: {
    config: () => request<{ products: Record<BillingProduct, string> }>("/api/billing/config"),
    subscriptions: () => request<Subscription[]>("/api/billing/subscriptions"),
    checkout: (product: BillingProduct) =>
      request<{ url: string }>("/api/billing/checkout", { method: "POST", body: JSON.stringify({ product }) }),
    portal: () => request<{ url: string }>("/api/billing/portal", { method: "POST" }),
  },
  // Stundenplan-Stammdaten (Raster, Dozenten, Fächer)
  stundenplan: {
    raster: {
      list: () => request<TimeSlot[]>("/api/stundenplan/raster"),
      save: (slots: { id?: number; label: string; startTime: string; endTime: string; ueCount: number }[]) =>
        request<TimeSlot[]>("/api/stundenplan/raster", { method: "PUT", body: JSON.stringify({ slots }) }),
      /** Länge einer Unterrichtseinheit in Minuten, Vorgabe 45. */
      ueLength: () => request<{ minutes: number }>("/api/stundenplan/raster/ue-laenge"),
      /** recalc: alle Blöcke rechnen ihre UE neu aus der Dauer. */
      setUeLength: (minutes: number, recalc: boolean) =>
        request<{ minutes: number; slots: TimeSlot[] }>("/api/stundenplan/raster/ue-laenge", { method: "PUT", body: JSON.stringify({ minutes, recalc }) }),
    },
    lecturers: {
      list: () => request<Lecturer[]>("/api/stundenplan/dozenten"),
      create: (d: any) => request<Lecturer>("/api/stundenplan/dozenten", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<Lecturer>(`/api/stundenplan/dozenten/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/stundenplan/dozenten/${id}`, { method: "DELETE" }),
      userOptions: () => request<{ id: string; firstName: string; lastName: string; email: string }[]>("/api/stundenplan/benutzer-auswahl"),
      csvTemplate: () => downloadFile("/api/stundenplan/dozenten/csv-template", "dozenten-vorlage.csv"),
      csvImport: (file: File) => uploadFile<CsvImportResult>("/api/stundenplan/dozenten/csv-import", file),
      availabilities: {
        list: (lecturerId: number) => request<LecturerAvailability[]>(`/api/stundenplan/dozenten/${lecturerId}/anwesenheiten`),
        create: (lecturerId: number, d: any) => request<LecturerAvailability>(`/api/stundenplan/dozenten/${lecturerId}/anwesenheiten`, { method: "POST", body: JSON.stringify(d) }),
        update: (lecturerId: number, id: number, d: any) => request<LecturerAvailability>(`/api/stundenplan/dozenten/${lecturerId}/anwesenheiten/${id}`, { method: "PUT", body: JSON.stringify(d) }),
        delete: (lecturerId: number, id: number) => request<void>(`/api/stundenplan/dozenten/${lecturerId}/anwesenheiten/${id}`, { method: "DELETE" }),
      },
      exceptions: {
        list: (lecturerId: number) => request<LecturerException[]>(`/api/stundenplan/dozenten/${lecturerId}/ausnahmen`),
        create: (lecturerId: number, d: any) => request<LecturerException>(`/api/stundenplan/dozenten/${lecturerId}/ausnahmen`, { method: "POST", body: JSON.stringify(d) }),
        update: (lecturerId: number, id: number, d: any) => request<LecturerException>(`/api/stundenplan/dozenten/${lecturerId}/ausnahmen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
        delete: (lecturerId: number, id: number) => request<void>(`/api/stundenplan/dozenten/${lecturerId}/ausnahmen/${id}`, { method: "DELETE" }),
      },
    },
    courseModels: {
      list: () => request<CourseModel[]>("/api/stundenplan/ausbildungsmodelle"),
      create: (d: any) => request<CourseModel>("/api/stundenplan/ausbildungsmodelle", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<CourseModel>(`/api/stundenplan/ausbildungsmodelle/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/stundenplan/ausbildungsmodelle/${id}`, { method: "DELETE" }),
    },
    /** Lehrplan eines Ausbildungsmodells. Jede Änderung liefert den ganzen Lehrplan zurück. */
    curriculum: {
      get: (modelId: number) => request<Curriculum>(`${LP}/${modelId}/lehrplan`),
      setTotal: (modelId: number, totalUe: number | null, poolUe: number | null) =>
        request<Curriculum>(`${LP}/${modelId}/lehrplan`, { method: "PUT", body: JSON.stringify({ totalUe, poolUe }) }),
      /** Pool-Stunden verteilen; die Liste ist vollständig, fehlende Fächer verlieren ihren Anteil. */
      setPool: (modelId: number, allocations: { entryId: number; poolUe: number }[]) =>
        request<Curriculum>(`${LP}/${modelId}/lehrplan/pool`, { method: "PUT", body: JSON.stringify({ allocations }) }),
      clear: (modelId: number) => request<Curriculum>(`${LP}/${modelId}/lehrplan`, { method: "DELETE" }),
      createGroup: (modelId: number, d: any) => request<Curriculum>(`${LP}/${modelId}/lehrplan/gruppen`, { method: "POST", body: JSON.stringify(d) }),
      updateGroup: (modelId: number, id: number, d: any) => request<Curriculum>(`${LP}/${modelId}/lehrplan/gruppen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      deleteGroup: (modelId: number, id: number) => request<Curriculum>(`${LP}/${modelId}/lehrplan/gruppen/${id}`, { method: "DELETE" }),
      addSubject: (modelId: number, d: any) => request<Curriculum>(`${LP}/${modelId}/lehrplan/faecher`, { method: "POST", body: JSON.stringify(d) }),
      addSubjects: (modelId: number, d: { subjectIds: number[]; groupId?: number | null; stage?: number | null }) =>
        request<{ added: number; skipped: number; view: Curriculum }>(`${LP}/${modelId}/lehrplan/faecher/sammel`, { method: "POST", body: JSON.stringify(d) }),
      updateSubject: (modelId: number, id: number, d: any) => request<Curriculum>(`${LP}/${modelId}/lehrplan/faecher/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      removeSubject: (modelId: number, id: number) => request<Curriculum>(`${LP}/${modelId}/lehrplan/faecher/${id}`, { method: "DELETE" }),
      reorder: (modelId: number, items: { type: "gruppe" | "fach"; id: number }[]) =>
        request<Curriculum>(`${LP}/${modelId}/lehrplan/reihenfolge`, { method: "PUT", body: JSON.stringify({ items }) }),
      copyFrom: (modelId: number, fromModelId: number) =>
        request<{ imported: number; skipped: number; view: Curriculum }>(`${LP}/${modelId}/lehrplan/kopieren`, { method: "POST", body: JSON.stringify({ fromModelId }) }),
      templates: (modelId: number) => request<CurriculumTemplateInfo[]>(`/api/stundenplan/lehrplan-vorlagen?${new URLSearchParams({ modell: String(modelId) })}`),
      applyTemplate: (modelId: number, templateId: number) =>
        request<{ imported: number; created: number; skipped: number; view: Curriculum }>(
          `${LP}/${modelId}/lehrplan/vorlage`, { method: "POST", body: JSON.stringify({ templateId }) }),
    },
    programs: () => request<TrainingPrograms>("/api/stundenplan/ausbildungsgaenge"),
    closures: {
      list: () => request<SchoolClosure[]>("/api/stundenplan/unterrichtsfrei"),
      create: (d: any) => request<SchoolClosure>("/api/stundenplan/unterrichtsfrei", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<SchoolClosure>(`/api/stundenplan/unterrichtsfrei/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/stundenplan/unterrichtsfrei/${id}`, { method: "DELETE" }),
    },
    plan: (von: string, bis: string, filter: { klasse?: number; dozent?: number; raum?: number } = {}) => {
      const p = new URLSearchParams({ von, bis });
      if (filter.klasse) p.set("klasse", String(filter.klasse));
      if (filter.dozent) p.set("dozent", String(filter.dozent));
      if (filter.raum) p.set("raum", String(filter.raum));
      return request<Lesson[]>(`/api/stundenplan/plan?${p}`);
    },
    /** Alles, was in einem Zeitraum vom Plan abweicht: Entfall und Vertretung. */
    changes: (von: string, bis: string, klasse?: number) => {
      const p = new URLSearchParams({ von, bis });
      if (klasse) p.set("klasse", String(klasse));
      return request<Lesson[]>(`/api/stundenplan/aenderungen?${p}`);
    },
    weekContext: (von: string, bis: string, klasse?: number) => {
      const p = new URLSearchParams({ von, bis });
      if (klasse) p.set("klasse", String(klasse));
      return request<{ days: { date: string; closures: string[]; practice: boolean }[] }>(
        `/api/stundenplan/wochenkontext?${p}`);
    },
    lecturerAvailability: (p: { von: string; bis?: string; startTime: string; endTime: string; klasse?: number; weekday?: number; intervalWeeks?: number }) => {
      const q = new URLSearchParams({ von: p.von, startTime: p.startTime, endTime: p.endTime });
      if (p.bis) q.set("bis", p.bis);
      if (p.klasse) q.set("klasse", String(p.klasse));
      if (p.weekday) q.set("weekday", String(p.weekday));
      if (p.intervalWeeks) q.set("intervalWeeks", String(p.intervalWeeks));
      return request<{ dates: number; lecturers: { id: number; known: boolean; missing: number }[] }>(
        `/api/stundenplan/dozenten-verfuegbarkeit?${q}`);
    },
    seriesRange: (klasse: number, fach: number) =>
      request<{ range: { start: string; end: string } | null; reason?: string; hoursPerWeek?: number | null }>(
        `/api/stundenplan/serien-zeitraum?${new URLSearchParams({ klasse: String(klasse), fach: String(fach) })}`),
    series: {
      get: (id: number) => request<LessonSeries>(`/api/stundenplan/serien/${id}`),
      preview: (d: any) => request<SeriesPreview>("/api/stundenplan/serien/vorschau", { method: "POST", body: JSON.stringify(d) }),
      create: (d: any) => request<{ id: number; created: number; conflicts: LessonConflict[] }>("/api/stundenplan/serien", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<{ id: number; created: number }>(`/api/stundenplan/serien/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<{ deleted: number }>(`/api/stundenplan/serien/${id}`, { method: "DELETE" }),
    },
    lessons: {
      create: (d: any) => request<{ lesson: Lesson; conflicts: LessonConflict[] }>("/api/stundenplan/termine", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<{ lesson: Lesson; conflicts: LessonConflict[] }>(`/api/stundenplan/termine/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/stundenplan/termine/${id}`, { method: "DELETE" }),
      /** Entfall oder Vertretung setzen, ohne den übrigen Termin anzufassen. */
      setStatus: (id: number, d: { status: LessonStatus; substituteLecturerId?: number | null; substituteRoomId?: number | null; changeNote?: string | null }) =>
        request<{ lesson: Lesson; conflicts: LessonConflict[] }>(`/api/stundenplan/termine/${id}/status`, { method: "PATCH", body: JSON.stringify(d) }),
      makeAllVisible: (d: any) => request<{ updated: number; notified: number }>("/api/stundenplan/termine/sichtbar-machen", { method: "POST", body: JSON.stringify(d) }),
      confirmAll: (d: any) => request<{ updated: number }>("/api/stundenplan/termine/bestaetigen", { method: "POST", body: JSON.stringify(d) }),
    },
    subjects: {
      list: () => request<Subject[]>("/api/stundenplan/faecher"),
      create: (d: any) => request<Subject>("/api/stundenplan/faecher", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<Subject>(`/api/stundenplan/faecher/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/stundenplan/faecher/${id}`, { method: "DELETE" }),
      /** Mehrere löschen; verplante Fächer bleiben und kommen in blocked zurück. */
      deleteMany: (subjectIds: number[]) =>
        request<{ deleted: number; blocked: { id: number; name: string; lessonCount: number }[] }>(
          "/api/stundenplan/faecher/loeschen", { method: "POST", body: JSON.stringify({ subjectIds }) }),
      csvTemplate: () => downloadFile("/api/stundenplan/faecher/csv-template", "faecher-vorlage.csv"),
      csvImport: (file: File) => uploadFile<CsvImportResult>("/api/stundenplan/faecher/csv-import", file),
    },
    rooms: {
      list: () => request<Room[]>("/api/stundenplan/raeume"),
      create: (d: any) => request<Room>("/api/stundenplan/raeume", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<Room>(`/api/stundenplan/raeume/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/stundenplan/raeume/${id}`, { method: "DELETE" }),
      csvTemplate: () => downloadFile("/api/stundenplan/raeume/csv-template", "raeume-vorlage.csv"),
      csvImport: (file: File) => uploadFile<CsvImportResult>("/api/stundenplan/raeume/csv-import", file),
      /** Freie Raeume fuer ein Zeitfenster, gefiltert nach Plaetzen und Ausstattung. */
      available: (p: { datum: string; von: string; bis: string; plaetze?: number; ausstattung?: number[]; ausser?: number }) => {
        const q = new URLSearchParams({ datum: p.datum, von: p.von, bis: p.bis });
        if (p.plaetze) q.set("plaetze", String(p.plaetze));
        if (p.ausstattung?.length) q.set("ausstattung", p.ausstattung.join(","));
        if (p.ausser) q.set("ausser", String(p.ausser));
        return request<RoomAvailability[]>(`/api/stundenplan/raeume/frei?${q}`);
      },
    },
    attendance: {
      /** Anwesenheitsmatrix einer Klasse für einen Zeitraum. */
      forClass: (klasse: number, von: string, bis: string) =>
        request<ClassAttendance>(`/api/stundenplan/klassen/${klasse}/anwesenheit?${new URLSearchParams({ von, bis })}`),
      list: (von: string, bis: string, klasse?: number) => {
        const p = new URLSearchParams({ von, bis });
        if (klasse) p.set("klasse", String(klasse));
        return request<StudentAbsence[]>(`/api/stundenplan/abwesenheiten?${p}`);
      },
      create: (d: any) => request<StudentAbsence>("/api/stundenplan/abwesenheiten", { method: "POST", body: JSON.stringify(d) }),
      /** Einstufen und Attest vermerken. */
      classify: (id: number, d: { kind?: AbsenceKind; attestVorgelegt?: boolean }) =>
        request<StudentAbsence>(`/api/stundenplan/abwesenheiten/${id}`, { method: "PATCH", body: JSON.stringify(d) }),
      retract: (id: number) =>
        request<StudentAbsence>(`/api/stundenplan/abwesenheiten/${id}/zuruecknehmen`, { method: "PATCH" }),
    },
    roomFeatures: {
      list: () => request<RoomFeature[]>("/api/stundenplan/raum-ausstattung"),
      create: (d: any) => request<RoomFeature>("/api/stundenplan/raum-ausstattung", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<RoomFeature>(`/api/stundenplan/raum-ausstattung/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/stundenplan/raum-ausstattung/${id}`, { method: "DELETE" }),
    },
  },
  auswertungen: {
    /**
     * Unterrichtsstunden je Klasse, Fach und Monat. Ohne von/bis liefert das Backend
     * die gesamte Ausbildung der gewaehlten Klassen.
     *
     * Mit schueler kommen zusaetzlich die versaeumten Stunden je Fach und Monat mit;
     * die Klasse ergibt sich dann aus dem Schueler und klassen wird ignoriert.
     */
    unterricht: (klassen: number[], von?: string, bis?: string, schueler?: number) => {
      const q = new URLSearchParams({ klassen: klassen.join(",") });
      if (von) q.set("von", von);
      if (bis) q.set("bis", bis);
      if (schueler) q.set("schueler", String(schueler));
      return request<LessonReport>(`/api/auswertungen/unterricht?${q}`);
    },
    /** Wünsche pro Praktikumsstelle und Fachbereich, mit Rang nach Eingang. */
    wuensche: () => request<WishReport>("/api/auswertungen/wuensche"),
  },
  // Öffentliche Registrierung (ohne Auth)
  register: {
    departmentGroups: () => requestPublic<{ id: number; name: string }[]>("/api/register/department-groups"),
    submit: (data: any) => requestPublic<{ message: string }>("/api/register", { method: "POST", body: JSON.stringify(data) }),
  },
  registerPractice: {
    submit: (data: any) => requestPublic<{ message: string; code: string }>("/api/register-practice", { method: "POST", body: JSON.stringify(data) }),
  },
  practiceOrg: {
    me: () => request<PracticeOrg>("/api/praktikumsstelle/me"),
    update: (data: Partial<PracticeOrg>) => request<PracticeOrg>("/api/praktikumsstelle/me", { method: "PUT", body: JSON.stringify(data) }),
    delete: (password: string) =>
      request<void>("/api/praktikumsstelle/me", { method: "DELETE", body: JSON.stringify({ password }) }),
    planung: (from?: string, to?: string) => {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const qs = params.toString();
      return request<{ from: string; to: string; items: PracticePlanningAssignment[] }>(
        `/api/praktikumsstelle/planung/zuweisungen${qs ? `?${qs}` : ""}`
      );
    },
    assignmentSteckbrief: (assignmentId: number) =>
      request<PracticeOrgAssignmentSteckbrief>(`/api/praktikumsstelle/zuweisungen/${assignmentId}/steckbrief`),
    documents: {
      list: (practiceId: number) =>
        request<StudentDocument[]>(`/api/praktikumsstelle/practices/${practiceId}/dokumente`),
      upload: (practiceId: number, file: File) =>
        uploadFile<StudentDocument>(`/api/praktikumsstelle/practices/${practiceId}/dokumente`, file),
      download: (practiceId: number, docId: number, filename: string) =>
        downloadFile(`/api/praktikumsstelle/practices/${practiceId}/dokumente/${docId}/download`, filename),
      delete: (practiceId: number, docId: number) =>
        request<void>(`/api/praktikumsstelle/practices/${practiceId}/dokumente/${docId}`, { method: "DELETE" }),
      rename: (practiceId: number, docId: number, originalName: string) =>
        request<StudentDocument>(`/api/praktikumsstelle/practices/${practiceId}/dokumente/${docId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ originalName }),
        }),
    },
  },
  practiceUsers: {
    list: () => request<PracticeUser[]>("/api/praktikumsstelle/benutzer"),
    create: (d: any) => request<PracticeUser & { emailSent?: boolean }>("/api/praktikumsstelle/benutzer", { method: "POST", body: JSON.stringify(d) }),
    update: (id: string, d: any) => request<any>(`/api/praktikumsstelle/benutzer/${id}`, { method: "PUT", body: JSON.stringify(d) }),
    toggle: (id: string, enabled: boolean) => request<any>(`/api/praktikumsstelle/benutzer/${id}/toggle`, { method: "PUT", body: JSON.stringify({ enabled }) }),
    resetPassword: (id: string) => request<any>(`/api/praktikumsstelle/benutzer/${id}/reset-password`, { method: "PUT" }),
    delete: (id: string) => request<void>(`/api/praktikumsstelle/benutzer/${id}`, { method: "DELETE" }),
  },
  // Plattform-Admin
  admin: {
    stats: () => request<PlatformStats>("/api/admin/statistik"),
    carriers: {
      list: () => request<AdminCarrier[]>("/api/admin/traeger"),
      get: (id: number, showEnter = false) => request<AdminCarrierDetail>(`/api/admin/traeger/${id}${showEnter ? "?betreten=1" : ""}`),
      detachSchool: (id: number, schoolId: number) => request<{ success: boolean }>(`/api/admin/traeger/${id}/schulen/${schoolId}`, { method: "DELETE" }),
      setFounder: (id: number, userId: string) => request<{ success: boolean }>(`/api/admin/traeger/${id}/haupt-admin`, { method: "PUT", body: JSON.stringify({ userId }) }),
      setOptions: (id: number, d: { maxSchools: number | null }) =>
        request<{ success: boolean; maxSchools: number | null }>(`/api/admin/traeger/${id}/optionen`, { method: "PUT", body: JSON.stringify(d) }),
    },
    apiDocs: () => request<any[]>("/api/admin/api-docs"),
    schools: {
      list: () => request<School[]>("/api/admin/schulen"),
      get: (id: number) => request<School>(`/api/admin/schulen/${id}`),
      create: (d: any) => request<School>("/api/admin/schulen", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<School>(`/api/admin/schulen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/admin/schulen/${id}`, { method: "DELETE" }),
      toggle: (id: number) => request<any>(`/api/admin/schulen/${id}/toggle-active`, { method: "PATCH" }),
      toggleSchuelerPortal: (id: number, enabled: boolean) =>
        request<{ success: boolean; enabled: boolean }>(`/api/admin/schulen/${id}/schuelerPortal`, { method: "PUT", body: JSON.stringify({ enabled }) }),
      togglePlanung: (id: number, enabled: boolean) =>
        request<{ success: boolean; enabled: boolean }>(`/api/admin/schulen/${id}/planung`, { method: "PUT", body: JSON.stringify({ enabled }) }),
      toggleStundenplan: (id: number, enabled: boolean) =>
        request<{ success: boolean; enabled: boolean }>(`/api/admin/schulen/${id}/stundenplan`, { method: "PUT", body: JSON.stringify({ enabled }) }),
      toggleDrk: (id: number, enabled: boolean) =>
        request<{ success: boolean; enabled: boolean }>(`/api/admin/schulen/${id}/drk`, { method: "PUT", body: JSON.stringify({ enabled }) }),
      updatePlanungTrial: (id: number, trialEndsAt: string | null) =>
        request<{ success: boolean }>(`/api/admin/schulen/${id}/planung-trial`, { method: "PUT", body: JSON.stringify({ trialEndsAt }) }),
      updateSchuelerPortalTrial: (id: number, trialEndsAt: string | null) =>
        request<{ success: boolean }>(`/api/admin/schulen/${id}/schuelerPortal-trial`, { method: "PUT", body: JSON.stringify({ trialEndsAt }) }),
      updateStundenplanTrial: (id: number, trialEndsAt: string | null) =>
        request<{ success: boolean }>(`/api/admin/schulen/${id}/stundenplan-trial`, { method: "PUT", body: JSON.stringify({ trialEndsAt }) }),
      csvTemplate: () => downloadFile("/api/admin/schulen/csv-template", "schulen-vorlage.csv"),
      csvImport: (file: File) => uploadFile<CsvImportResult>("/api/admin/schulen/csv-import", file),
      users: {
        list: (schoolId: number) => request<SchoolUser[]>(`/api/admin/schulen/${schoolId}/benutzer`),
        create: (schoolId: number, d: any) => request<SchoolUser>(`/api/admin/schulen/${schoolId}/benutzer`, { method: "POST", body: JSON.stringify(d) }),
        update: (schoolId: number, userId: string, d: any) => request<any>(`/api/admin/schulen/${schoolId}/benutzer/${userId}`, { method: "PUT", body: JSON.stringify(d) }),
        toggle: (schoolId: number, userId: string, enabled: boolean) => request<any>(`/api/admin/schulen/${schoolId}/benutzer/${userId}/toggle`, { method: "PUT", body: JSON.stringify({ enabled }) }),
        resetPassword: (schoolId: number, userId: string, password: string) => request<any>(`/api/admin/schulen/${schoolId}/benutzer/${userId}/reset-password`, { method: "PUT", body: JSON.stringify({ password }) }),
        delete: (schoolId: number, userId: string) => request<void>(`/api/admin/schulen/${schoolId}/benutzer/${userId}`, { method: "DELETE" }),
      },
      departments: {
        list: (schoolId: number) => request<{
          departmentGroupName: string | null;
          departments: AdminSchoolDepartment[];
          standardGroups: { id: number; name: string }[];
          customGroups: { id: number; name: string }[];
        }>(`/api/admin/schulen/${schoolId}/fachbereiche`),
        delete: (schoolId: number, departmentId: string) => request<void>(`/api/admin/schulen/${schoolId}/fachbereiche/${encodeURIComponent(departmentId)}`, { method: "DELETE" }),
        setGroup: (schoolId: number, departmentId: string, kind: "standard" | "custom" | "none", groupId?: number) =>
          request<{ success: boolean }>(`/api/admin/schulen/${schoolId}/fachbereiche/${encodeURIComponent(departmentId)}/gruppe`, { method: "PUT", body: JSON.stringify({ kind, groupId }) }),
      },
      customGroups: {
        list: (schoolId: number) => request<{ id: number; name: string }[]>(`/api/admin/schulen/${schoolId}/fachbereich-gruppen`),
        delete: (schoolId: number, groupId: number) => request<void>(`/api/admin/schulen/${schoolId}/fachbereich-gruppen/${groupId}`, { method: "DELETE" }),
      },
    },
    practices: {
      list: () => request<PracticeOrgAdmin[]>("/api/admin/praktikumsstellen"),
      get: (id: number) => request<PracticeOrgAdminDetail>(`/api/admin/praktikumsstellen/${id}`),
      create: (d: any) => request<PracticeOrgAdmin>("/api/admin/praktikumsstellen", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<PracticeOrgAdmin>(`/api/admin/praktikumsstellen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      toggleActive: (id: number) => request<{ id: number; active: boolean }>(`/api/admin/praktikumsstellen/${id}/toggle-active`, { method: "PATCH" }),
      delete: (id: number) => request<void>(`/api/admin/praktikumsstellen/${id}`, { method: "DELETE" }),
      users: {
        list: (orgId: number) => request<PracticeUser[]>(`/api/admin/praktikumsstellen/${orgId}/benutzer`),
        create: (orgId: number, d: any) => request<PracticeUser & { emailSent?: boolean }>(`/api/admin/praktikumsstellen/${orgId}/benutzer`, { method: "POST", body: JSON.stringify(d) }),
        update: (orgId: number, userId: string, d: any) => request<any>(`/api/admin/praktikumsstellen/${orgId}/benutzer/${userId}`, { method: "PUT", body: JSON.stringify(d) }),
        toggle: (orgId: number, userId: string, enabled: boolean) => request<any>(`/api/admin/praktikumsstellen/${orgId}/benutzer/${userId}/toggle`, { method: "PUT", body: JSON.stringify({ enabled }) }),
        resetPassword: (orgId: number, userId: string) => request<{ success: boolean; emailSent: boolean; tempPassword?: string }>(`/api/admin/praktikumsstellen/${orgId}/benutzer/${userId}/reset-password`, { method: "PUT" }),
        delete: (orgId: number, userId: string) => request<void>(`/api/admin/praktikumsstellen/${orgId}/benutzer/${userId}`, { method: "DELETE" }),
      },
    },
    departments: {
      list: () => request<Department[]>("/api/admin/fachbereiche"),
      create: (d: any) => request<Department>("/api/admin/fachbereiche", { method: "POST", body: JSON.stringify(d) }),
      update: (id: string, d: any) => request<Department>(`/api/admin/fachbereiche/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: string) => request<void>(`/api/admin/fachbereiche/${id}`, { method: "DELETE" }),
    },
    departmentGroups: {
      list: () => request<DepartmentGroup[]>("/api/admin/fachbereich-gruppen"),
      create: (d: any) => request<DepartmentGroup>("/api/admin/fachbereich-gruppen", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<DepartmentGroup>(`/api/admin/fachbereich-gruppen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/admin/fachbereich-gruppen/${id}`, { method: "DELETE" }),
    },
    curriculumTemplates: {
      list: () => request<CurriculumTemplate[]>("/api/admin/lehrplan-vorlagen"),
      create: (d: any) => request<CurriculumTemplate>("/api/admin/lehrplan-vorlagen", { method: "POST", body: JSON.stringify(d) }),
      update: (id: number, d: any) => request<CurriculumTemplate>(`/api/admin/lehrplan-vorlagen/${id}`, { method: "PUT", body: JSON.stringify(d) }),
      delete: (id: number) => request<void>(`/api/admin/lehrplan-vorlagen/${id}`, { method: "DELETE" }),
      saveContent: (id: number, d: any) => request<CurriculumTemplate>(`/api/admin/lehrplan-vorlagen/${id}/inhalt`, { method: "PUT", body: JSON.stringify(d) }),
      csvTemplate: () => downloadFile("/api/admin/lehrplan-vorlagen/csv-template", "lehrplan-vorlage.csv"),
      csvImport: (id: number, file: File) => uploadFile<CsvImportResult>(`/api/admin/lehrplan-vorlagen/${id}/csv-import`, file),
      /** JSON-Datei zum Übertragen auf ein anderes System (Test → Produktion). */
      exportFile: (ids: number[]) => downloadFile(
        `/api/admin/lehrplan-vorlagen/export?${new URLSearchParams({ ids: ids.join(",") })}`,
        `lehrplan-vorlagen-${new Date().toISOString().slice(0, 10)}.json`),
      importData: (data: unknown) => request<{ created: number; updated: number; warnings: string[] }>(
        "/api/admin/lehrplan-vorlagen/import", { method: "POST", body: JSON.stringify(data) }),
    },
  },
};
